import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Editor, { DiffEditor } from "@monaco-editor/react";
import prettier from "prettier/standalone";
import prettierPluginBabel from "prettier/plugins/babel";
import prettierPluginEstree from "prettier/plugins/estree";
import prettierPluginHtml from "prettier/plugins/html";
import prettierPluginPostcss from "prettier/plugins/postcss";
import yaml from "js-yaml";
import xmlFormatter from "xml-formatter";
import { minify as minifyCss } from "csso";
import { minify as minifyJs } from "terser";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import DataObjectIcon from "@mui/icons-material/DataObject";
import CodeIcon from "@mui/icons-material/Code";
import HtmlIcon from "@mui/icons-material/Html";
import CssIcon from "@mui/icons-material/Css";
import JavascriptIcon from "@mui/icons-material/Javascript";
import KeyIcon from "@mui/icons-material/Key";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CompressIcon from "@mui/icons-material/Compress";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import SearchIcon from "@mui/icons-material/Search";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DifferenceIcon from "@mui/icons-material/Difference";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import Footer from "../components/Footer";
import { updateMetaTags } from "../utils/seo";
import BackToWorldButton from "../components/BackToWorldButton";

type FormatterToolId =
  | "json"
  | "xml"
  | "html"
  | "css"
  | "javascript"
  | "base64";

type ViewMode = "raw" | "tree";
type EditorTheme = "light" | "dark";
type ActiveEditor = "input" | "output" | "compare";
type ActionType = "format" | "minify" | "validate";
type JsonConvertTarget = "yaml" | "csv" | "typescript";
type IndentOption = "2" | "4" | "tab";

interface ToolDefinition {
  id: FormatterToolId;
  label: string;
  description: string;
  language: string;
  extension: string;
  icon: React.ReactNode;
  supportsMinify: boolean;
  supportsValidate: boolean;
}

interface ToolState {
  input: string;
  output: string;
  error: string | null;
  notice: string | null;
  outputLanguage?: string;
  outputExtension?: string;
  lastLoadedFileName?: string;
}

interface JsonValidationInfo {
  valid: boolean;
  message: string;
  line?: number;
  column?: number;
  index?: number;
}

interface JsonMetadata {
  totalKeys: number;
  objectCount: number;
  arrayCount: number;
  primitiveCount: number;
  totalArrayItems: number;
  maxArraySize: number;
  maxDepth: number;
}

interface JsonTreeNodeProps {
  value: unknown;
  nodeKey?: string;
  path: string;
  depth: number;
  expandedPaths: Set<string>;
  togglePath: (path: string) => void;
  searchQuery: string;
}

const TOOL_DEFINITIONS: ToolDefinition[] = [
  {
    id: "json",
    label: "JSON Formatter",
    description: "Beautify, validate, minify, diff, search and inspect JSON.",
    language: "json",
    extension: "json",
    icon: <DataObjectIcon color="primary" />,
    supportsMinify: true,
    supportsValidate: true,
  },
  {
    id: "xml",
    label: "XML Formatter",
    description: "Format and validate XML documents locally in the browser.",
    language: "xml",
    extension: "xml",
    icon: <CodeIcon color="primary" />,
    supportsMinify: true,
    supportsValidate: true,
  },
  {
    id: "html",
    label: "HTML Formatter",
    description: "Beautify or minify HTML fragments and full documents.",
    language: "html",
    extension: "html",
    icon: <HtmlIcon color="primary" />,
    supportsMinify: true,
    supportsValidate: true,
  },
  {
    id: "css",
    label: "CSS Formatter",
    description: "Format and minify CSS with copy-friendly output.",
    language: "css",
    extension: "css",
    icon: <CssIcon color="primary" />,
    supportsMinify: true,
    supportsValidate: true,
  },
  {
    id: "javascript",
    label: "JavaScript Formatter",
    description: "Format, compact and validate JavaScript with Prettier.",
    language: "javascript",
    extension: "js",
    icon: <JavascriptIcon color="primary" />,
    supportsMinify: true,
    supportsValidate: true,
  },
  {
    id: "base64",
    label: "Base64 Utility",
    description:
      "Encode, decode and validate Base64 text entirely client-side.",
    language: "plaintext",
    extension: "txt",
    icon: <KeyIcon color="primary" />,
    supportsMinify: true,
    supportsValidate: true,
  },
];

const DEFAULT_TOOL_STATES: Record<FormatterToolId, ToolState> = {
  json: {
    input:
      '{\n  "developer": "toolkit",\n  "features": ["format", "validate", "diff"],\n  "clientSide": true\n}',
    output: "",
    error: null,
    notice: "Ready to format JSON.",
  },
  xml: {
    input:
      "<root><name>Formatter Toolkit</name><mode>client-side</mode></root>",
    output: "",
    error: null,
    notice: "Ready to format XML.",
  },
  html: {
    input:
      "<section><h1>Developer Toolkit</h1><p>Format HTML locally.</p></section>",
    output: "",
    error: null,
    notice: "Ready to format HTML.",
  },
  css: {
    input: ".toolkit{display:grid;gap:12px;color:#91bd3a}",
    output: "",
    error: null,
    notice: "Ready to format CSS.",
  },
  javascript: {
    input: "const toolkit={name:'Formatter Toolkit',format(){return true}};",
    output: "",
    error: null,
    notice: "Ready to format JavaScript.",
  },
  base64: {
    input: "Developer Formatter Toolkit",
    output: "",
    error: null,
    notice: "Ready to encode or decode Base64.",
  },
};

const FAQ_ITEMS = [
  {
    question: "Does the formatter toolkit upload code or data to a server?",
    answer:
      "No. Formatting, validation, minification, conversions and diffing run entirely in your browser, so your code never leaves your device.",
  },
  {
    question: "Which formats are supported?",
    answer:
      "The toolkit includes JSON, XML, HTML, CSS, JavaScript and Base64 tools, all with a shared copy-friendly editor workflow.",
  },
  {
    question: "Can I format files by dragging and dropping them?",
    answer:
      "Yes. You can drop .json, .txt, .xml, .html, .css or .js files directly into the editor area and the tool will load them locally.",
  },
  {
    question: "Is diff mode available for JSON?",
    answer:
      "Yes. The JSON tool includes a client-side diff workspace so you can compare two JSON payloads and inspect formatted differences without using a backend service.",
  },
] as const;

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getIndentString = (indent: IndentOption) => {
  if (indent === "tab") {
    return "\t";
  }
  return " ".repeat(Number(indent));
};

const triggerDownload = (content: string, fileName: string) => {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

const inferToolFromFileName = (fileName: string): FormatterToolId | null => {
  const normalized = fileName.toLowerCase();
  if (normalized.endsWith(".json")) return "json";
  if (normalized.endsWith(".xml")) return "xml";
  if (normalized.endsWith(".html") || normalized.endsWith(".htm"))
    return "html";
  if (normalized.endsWith(".css")) return "css";
  if (
    normalized.endsWith(".js") ||
    normalized.endsWith(".mjs") ||
    normalized.endsWith(".cjs")
  ) {
    return "javascript";
  }
  if (normalized.endsWith(".txt")) return null;
  return null;
};

const getLineColumnFromIndex = (source: string, index: number) => {
  const safeIndex = Math.max(0, Math.min(index, source.length));
  const before = source.slice(0, safeIndex);
  const lines = before.split("\n");
  return {
    line: lines.length,
    column: lines[lines.length - 1].length + 1,
  };
};

const validateJsonInput = (input: string): JsonValidationInfo => {
  if (!input.trim()) {
    return { valid: false, message: "JSON input is empty." };
  }

  try {
    JSON.parse(input);
    return { valid: true, message: "Valid JSON." };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Invalid JSON.";
    const match = message.match(/position\s+(\d+)/i);
    if (match) {
      const index = Number(match[1]);
      const { line, column } = getLineColumnFromIndex(input, index);
      return {
        valid: false,
        message: `${message} (line ${line}, column ${column})`,
        line,
        column,
        index,
      };
    }

    return { valid: false, message };
  }
};

const computeJsonMetadata = (value: unknown): JsonMetadata => {
  const metadata: JsonMetadata = {
    totalKeys: 0,
    objectCount: 0,
    arrayCount: 0,
    primitiveCount: 0,
    totalArrayItems: 0,
    maxArraySize: 0,
    maxDepth: 0,
  };

  const walk = (node: unknown, depth: number) => {
    metadata.maxDepth = Math.max(metadata.maxDepth, depth);

    if (Array.isArray(node)) {
      metadata.arrayCount += 1;
      metadata.totalArrayItems += node.length;
      metadata.maxArraySize = Math.max(metadata.maxArraySize, node.length);
      node.forEach((item) => walk(item, depth + 1));
      return;
    }

    if (node !== null && typeof node === "object") {
      metadata.objectCount += 1;
      const entries = Object.entries(node as Record<string, unknown>);
      metadata.totalKeys += entries.length;
      entries.forEach(([, child]) => walk(child, depth + 1));
      return;
    }

    metadata.primitiveCount += 1;
  };

  walk(value, 1);
  return metadata;
};

const collectTreePaths = (value: unknown, path = "root"): string[] => {
  const paths = [path];

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      paths.push(...collectTreePaths(item, `${path}[${index}]`));
    });
  } else if (value !== null && typeof value === "object") {
    Object.entries(value as Record<string, unknown>).forEach(([key, child]) => {
      paths.push(...collectTreePaths(child, `${path}.${key}`));
    });
  }

  return paths;
};

const highlightMatches = (value: string, query: string) => {
  if (!query.trim()) {
    return value;
  }

  const matcher = new RegExp(`(${escapeRegExp(query)})`, "ig");
  const parts = value.split(matcher);

  return parts.map((part, index) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <Box
        key={`${part}-${index}`}
        component="mark"
        sx={{
          backgroundColor: "rgba(145, 189, 58, 0.28)",
          color: "inherit",
          px: 0.25,
          borderRadius: 0.5,
        }}
      >
        {part}
      </Box>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    ),
  );
};

const matchesSearch = (value: unknown, query: string): boolean => {
  if (!query.trim()) {
    return true;
  }

  const normalized = query.toLowerCase();

  if (Array.isArray(value)) {
    return value.some((item) => matchesSearch(item, query));
  }

  if (value !== null && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).some(
      ([key, child]) =>
        key.toLowerCase().includes(normalized) || matchesSearch(child, query),
    );
  }

  return String(value).toLowerCase().includes(normalized);
};

const flattenObject = (
  value: Record<string, unknown>,
  prefix = "",
): Record<string, string> => {
  const flattened: Record<string, string> = {};

  Object.entries(value).forEach(([key, child]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key;

    if (Array.isArray(child)) {
      flattened[nextKey] = JSON.stringify(child);
      return;
    }

    if (child !== null && typeof child === "object") {
      Object.assign(
        flattened,
        flattenObject(child as Record<string, unknown>, nextKey),
      );
      return;
    }

    flattened[nextKey] = child === undefined ? "" : String(child);
  });

  return flattened;
};

const convertJsonToCsv = (value: unknown) => {
  const rows = Array.isArray(value) ? value : [value];
  const flattenedRows = rows.map((row) =>
    row !== null && typeof row === "object"
      ? flattenObject(row as Record<string, unknown>)
      : { value: JSON.stringify(row) },
  );
  const headers = Array.from(
    new Set(flattenedRows.flatMap((row) => Object.keys(row))),
  );

  const escapeCsvCell = (cell: string) => {
    const normalized = cell ?? "";
    return /[",\n]/.test(normalized)
      ? `"${normalized.replace(/"/g, '""')}"`
      : normalized;
  };

  const lines = [headers.join(",")];
  flattenedRows.forEach((row) => {
    lines.push(
      headers.map((header) => escapeCsvCell(row[header] ?? "")).join(","),
    );
  });
  return lines.join("\n");
};

const toPascalCase = (value: string) =>
  value
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("") || "GeneratedType";

const generateTsType = (value: unknown, nameHint = "Root"): string => {
  if (Array.isArray(value)) {
    if (!value.length) {
      return "unknown[]";
    }

    const childTypes = Array.from(
      new Set(value.map((item) => generateTsType(item, `${nameHint}Item`))),
    );
    return childTypes.length === 1
      ? `${childTypes[0]}[]`
      : `(${childTypes.join(" | ")})[]`;
  }

  if (value === null) {
    return "null";
  }

  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (!entries.length) {
      return "Record<string, never>";
    }

    const members = entries
      .map(([key, child]) => {
        const safeKey = /^[a-zA-Z_$][\w$]*$/.test(key) ? key : `'${key}'`;
        return `  ${safeKey}: ${generateTsType(child, toPascalCase(key))};`;
      })
      .join("\n");

    return `{\n${members}\n}`;
  }

  if (typeof value === "string") return "string";
  if (typeof value === "number")
    return Number.isInteger(value) ? "number" : "number";
  if (typeof value === "boolean") return "boolean";
  return "unknown";
};

const convertJsonToTypeScript = (value: unknown) => {
  const rootName = "RootPayload";
  return `export interface ${rootName} ${generateTsType(value, rootName)}`;
};

const utf8ToBase64 = (value: string) => {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary);
};

const base64ToUtf8 = (value: string) => {
  const binary = window.atob(value);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

const validateXml = (value: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(value, "application/xml");
  const errorNode = doc.querySelector("parsererror");
  if (errorNode) {
    throw new Error(errorNode.textContent?.trim() || "Invalid XML.");
  }
  return doc;
};

const validateHtml = (value: string) => {
  const parser = new DOMParser();
  parser.parseFromString(value, "text/html");
  return true;
};

const minifyHtmlClient = (value: string) =>
  value
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/>\s+</g, "><")
    .replace(/\s{2,}/g, " ")
    .trim();

const validateCss = (value: string) => {
  if (typeof CSSStyleSheet === "undefined") {
    return true;
  }

  const stylesheet = new CSSStyleSheet();
  stylesheet.replaceSync(value);
  return true;
};

const validateJavaScript = (value: string) => {
  // eslint-disable-next-line no-new, no-new-func
  new Function(value);
  return true;
};

const JsonTreeNode: React.FC<JsonTreeNodeProps> = ({
  value,
  nodeKey,
  path,
  depth,
  expandedPaths,
  togglePath,
  searchQuery,
}) => {
  const isExpandable =
    Array.isArray(value) || (value !== null && typeof value === "object");
  const isExpanded = expandedPaths.has(path);
  const hasMatch =
    !searchQuery.trim() ||
    matchesSearch(value, searchQuery) ||
    nodeKey?.toLowerCase().includes(searchQuery.toLowerCase());

  if (!hasMatch) {
    return null;
  }

  const renderPrimitive = (primitive: unknown) => {
    const normalized = JSON.stringify(primitive);
    return (
      <Typography
        component="span"
        variant="body2"
        sx={{ color: primitive === null ? "error.main" : "text.primary" }}
      >
        {highlightMatches(normalized, searchQuery)}
      </Typography>
    );
  };

  const summary = Array.isArray(value)
    ? `[${value.length}]`
    : value !== null && typeof value === "object"
      ? `{${Object.keys(value as Record<string, unknown>).length}}`
      : null;

  return (
    <Box
      sx={{
        pl: depth === 0 ? 0 : 2,
        borderLeft: depth > 0 ? "1px solid" : "none",
        borderColor: "divider",
        ml: depth > 0 ? 1 : 0,
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ py: 0.5 }}
      >
        {isExpandable ? (
          <IconButton
            size="small"
            onClick={() => togglePath(path)}
            sx={{ p: 0.25 }}
          >
            {isExpanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 24 }} />
        )}
        {nodeKey !== undefined && (
          <Typography
            component="span"
            variant="body2"
            sx={{ color: "primary.main", fontWeight: 700 }}
          >
            {highlightMatches(nodeKey, searchQuery)}:
          </Typography>
        )}
        {isExpandable ? (
          <Typography component="span" variant="body2" color="text.secondary">
            {summary}
          </Typography>
        ) : (
          renderPrimitive(value)
        )}
      </Stack>

      {isExpandable && isExpanded && (
        <Box>
          {Array.isArray(value)
            ? value.map((item, index) => (
                <JsonTreeNode
                  key={`${path}-${index}`}
                  value={item}
                  nodeKey={String(index)}
                  path={`${path}[${index}]`}
                  depth={depth + 1}
                  expandedPaths={expandedPaths}
                  togglePath={togglePath}
                  searchQuery={searchQuery}
                />
              ))
            : Object.entries(value as Record<string, unknown>).map(
                ([key, child]) => (
                  <JsonTreeNode
                    key={`${path}-${key}`}
                    value={child}
                    nodeKey={key}
                    path={`${path}.${key}`}
                    depth={depth + 1}
                    expandedPaths={expandedPaths}
                    togglePath={togglePath}
                    searchQuery={searchQuery}
                  />
                ),
              )}
        </Box>
      )}
    </Box>
  );
};

const DeveloperFormatterToolkit: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const inputEditorRef = useRef<any>(null);
  const outputEditorRef = useRef<any>(null);
  const compareEditorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);
  const outputDecorationIdsRef = useRef<string[]>([]);
  const actionRef = useRef<((action: ActionType) => Promise<void>) | null>(
    null,
  );
  const [activeTool, setActiveTool] = useState<FormatterToolId>("json");
  const [toolStates, setToolStates] =
    useState<Record<FormatterToolId, ToolState>>(DEFAULT_TOOL_STATES);
  const [indent, setIndent] = useState<IndentOption>("2");
  const [jsonViewMode, setJsonViewMode] = useState<ViewMode>("raw");
  const [jsonSearchQuery, setJsonSearchQuery] = useState("");
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(
    new Set(["root"]),
  );
  const [diffMode, setDiffMode] = useState(false);
  const [compareInput, setCompareInput] = useState('{\n  "developer": true\n}');
  const [compareOutput, setCompareOutput] = useState("");
  const [compareError, setCompareError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [editorTheme, setEditorTheme] = useState<EditorTheme>("light");
  const [formatOnPaste, setFormatOnPaste] = useState(true);
  const [activeEditor, setActiveEditor] = useState<ActiveEditor>("input");
  const [jsonConvertTarget, setJsonConvertTarget] =
    useState<JsonConvertTarget>("yaml");
  const [busy, setBusy] = useState(false);
  const [pageMessage, setPageMessage] = useState<string | null>(null);

  const toolDefinition =
    TOOL_DEFINITIONS.find((tool) => tool.id === activeTool) ??
    TOOL_DEFINITIONS[0];
  const currentToolState = toolStates[activeTool];
  const currentOutputLanguage =
    currentToolState.outputLanguage ?? toolDefinition.language;
  const jsonValidation = useMemo(
    () =>
      activeTool === "json" ? validateJsonInput(currentToolState.input) : null,
    [activeTool, currentToolState.input],
  );

  const jsonTreeData = useMemo(() => {
    if (activeTool !== "json") {
      return null;
    }

    const candidates = [currentToolState.output, currentToolState.input].filter(
      Boolean,
    );
    for (const candidate of candidates) {
      const validation = validateJsonInput(candidate);
      if (validation.valid) {
        return JSON.parse(candidate);
      }
    }

    return null;
  }, [activeTool, currentToolState.input, currentToolState.output]);

  const jsonMetadata = useMemo(
    () => (jsonTreeData !== null ? computeJsonMetadata(jsonTreeData) : null),
    [jsonTreeData],
  );

  const updateToolState = useCallback(
    (
      toolId: FormatterToolId,
      updater: Partial<ToolState> | ((current: ToolState) => ToolState),
    ) => {
      setToolStates((current) => ({
        ...current,
        [toolId]:
          typeof updater === "function"
            ? updater(current[toolId])
            : { ...current[toolId], ...updater },
      }));
    },
    [],
  );

  useEffect(() => {
    updateMetaTags({
      title:
        "JSON Formatter & Developer Code Formatter Toolkit | Shashi Sharma",
      description:
        "Client-side JSON formatter, validator, minifier and developer code formatter toolkit with XML, HTML, CSS, JavaScript and Base64 tools. Paste, format, diff, convert and download locally.",
      keywords:
        "json formatter, json validator, json minifier, xml formatter, html formatter, css formatter, javascript formatter, base64 encoder, developer toolkit, code beautifier",
      canonical: `${window.location.origin}/developer-formatter-toolkit`,
      ogTitle: "JSON Formatter & Developer Code Formatter Toolkit",
      ogDescription:
        "A client-side developer formatter toolkit with Monaco editors, JSON tree view, diff mode, conversions and code formatting utilities.",
      ogUrl: `${window.location.origin}/developer-formatter-toolkit`,
      ogType: "website",
      ogImage: `${window.location.origin}/favicon.png`,
      twitterCard: "summary_large_image",
      twitterTitle: "JSON Formatter & Developer Code Formatter Toolkit",
      twitterDescription:
        "Format, validate, minify, diff and convert JSON, XML, HTML, CSS, JavaScript and Base64 locally in the browser.",
      twitterImage: `${window.location.origin}/favicon.png`,
      structuredData: JSON.stringify([
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Developer Formatter Toolkit",
          applicationCategory: "DeveloperApplication",
          operatingSystem: "Any",
          url: `${window.location.origin}/developer-formatter-toolkit`,
          description:
            "A client-side developer formatter toolkit featuring JSON formatting, validation, minification, diffing, tree view and additional code formatter tools.",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
          featureList: [
            "JSON formatter and validator",
            "JSON tree view and diff mode",
            "JSON to YAML CSV TypeScript conversion",
            "XML HTML CSS JavaScript formatters",
            "Base64 encode decode utility",
            "Client-side Monaco editors with file upload and copy download actions",
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ_ITEMS.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        },
      ]),
    });
  }, []);

  useEffect(() => {
    if (!jsonTreeData) {
      setExpandedPaths(new Set(["root"]));
      return;
    }

    setExpandedPaths(new Set(collectTreePaths(jsonTreeData).slice(0, 12)));
  }, [jsonTreeData, activeTool]);

  const setInputMarkers = useCallback(
    (validation: JsonValidationInfo | null) => {
      if (!monacoRef.current || !inputEditorRef.current) {
        return;
      }

      const model = inputEditorRef.current.getModel?.();
      if (!model) {
        return;
      }

      const markers =
        validation && !validation.valid && validation.line && validation.column
          ? [
              {
                startLineNumber: validation.line,
                startColumn: validation.column,
                endLineNumber: validation.line,
                endColumn: validation.column + 1,
                message: validation.message,
                severity: monacoRef.current.MarkerSeverity.Error,
              },
            ]
          : [];

      monacoRef.current.editor.setModelMarkers(
        model,
        "json-validation",
        markers,
      );
    },
    [],
  );

  useEffect(() => {
    if (activeTool === "json") {
      setInputMarkers(jsonValidation);
    } else if (monacoRef.current && inputEditorRef.current?.getModel?.()) {
      monacoRef.current.editor.setModelMarkers(
        inputEditorRef.current.getModel(),
        "json-validation",
        [],
      );
    }
  }, [activeTool, jsonValidation, setInputMarkers]);

  useEffect(() => {
    if (
      activeTool !== "json" ||
      jsonViewMode !== "raw" ||
      !monacoRef.current ||
      !outputEditorRef.current
    ) {
      if (outputEditorRef.current && outputDecorationIdsRef.current.length) {
        outputDecorationIdsRef.current =
          outputEditorRef.current.deltaDecorations(
            outputDecorationIdsRef.current,
            [],
          );
      }
      return;
    }

    const model = outputEditorRef.current.getModel?.();
    if (!model) {
      return;
    }

    if (!jsonSearchQuery.trim()) {
      outputDecorationIdsRef.current = outputEditorRef.current.deltaDecorations(
        outputDecorationIdsRef.current,
        [],
      );
      return;
    }

    const matches = model.findMatches(
      jsonSearchQuery,
      false,
      false,
      false,
      null,
      true,
    );

    outputDecorationIdsRef.current = outputEditorRef.current.deltaDecorations(
      outputDecorationIdsRef.current,
      matches.map((match: any) => ({
        range: match.range,
        options: {
          inlineClassName: "json-search-highlight",
        },
      })),
    );
  }, [activeTool, currentToolState.output, jsonSearchQuery, jsonViewMode]);

  const formatWithPrettier = useCallback(
    async (value: string, parser: "html" | "css" | "babel") => {
      const parserPlugins =
        parser === "html"
          ? [prettierPluginHtml]
          : parser === "css"
            ? [prettierPluginPostcss]
            : [prettierPluginBabel, prettierPluginEstree];

      return prettier.format(value, {
        parser,
        plugins: parserPlugins,
        tabWidth: indent === "tab" ? 2 : Number(indent),
        useTabs: indent === "tab",
        singleQuote: true,
      });
    },
    [indent],
  );

  const runAction = useCallback(
    async (action: ActionType) => {
      const input = currentToolState.input;
      const indentString = getIndentString(indent);

      if (!input.trim()) {
        updateToolState(activeTool, {
          error: "Input is empty.",
          notice: null,
        });
        return;
      }

      setBusy(true);
      setPageMessage(null);

      try {
        if (activeTool === "json") {
          const validation = validateJsonInput(input);
          if (!validation.valid) {
            updateToolState(activeTool, {
              error: validation.message,
              notice: null,
            });
            return;
          }

          const parsed = JSON.parse(input);
          let output = currentToolState.output;
          let outputLanguage = "json";
          let outputExtension = "json";
          let notice = "Valid JSON.";

          if (action === "format") {
            output = JSON.stringify(parsed, null, indentString);
            notice = `JSON formatted with ${indent === "tab" ? "tabs" : `${indent} spaces`} indentation.`;
          }

          if (action === "minify") {
            output = JSON.stringify(parsed);
            notice = "JSON minified successfully.";
          }

          if (action === "validate") {
            output = JSON.stringify(parsed, null, indentString);
          }

          updateToolState(activeTool, {
            output,
            error: null,
            notice,
            outputLanguage,
            outputExtension,
          });
          return;
        }

        if (activeTool === "xml") {
          validateXml(input);
          if (action === "validate") {
            updateToolState(activeTool, {
              output: input,
              error: null,
              notice: "Valid XML document.",
              outputLanguage: "xml",
              outputExtension: "xml",
            });
            return;
          }

          const output =
            action === "format"
              ? xmlFormatter(input, {
                  indentation:
                    indent === "tab" ? "\t" : " ".repeat(Number(indent)),
                  collapseContent: true,
                })
              : input.replace(/>\s+</g, "><").trim();

          updateToolState(activeTool, {
            output,
            error: null,
            notice:
              action === "format"
                ? "XML formatted successfully."
                : "XML minified successfully.",
            outputLanguage: "xml",
            outputExtension: "xml",
          });
          return;
        }

        if (activeTool === "html") {
          validateHtml(input);
          if (action === "validate") {
            updateToolState(activeTool, {
              output: input,
              error: null,
              notice: "HTML parsed successfully in the browser.",
              outputLanguage: "html",
              outputExtension: "html",
            });
            return;
          }

          const output =
            action === "format"
              ? await formatWithPrettier(input, "html")
              : minifyHtmlClient(input);

          updateToolState(activeTool, {
            output,
            error: null,
            notice:
              action === "format"
                ? "HTML formatted successfully."
                : "HTML minified successfully.",
            outputLanguage: "html",
            outputExtension: "html",
          });
          return;
        }

        if (activeTool === "css") {
          validateCss(input);
          if (action === "validate") {
            updateToolState(activeTool, {
              output: input,
              error: null,
              notice: "CSS parsed successfully.",
              outputLanguage: "css",
              outputExtension: "css",
            });
            return;
          }

          const output =
            action === "format"
              ? await formatWithPrettier(input, "css")
              : minifyCss(input).css;

          updateToolState(activeTool, {
            output,
            error: null,
            notice:
              action === "format"
                ? "CSS formatted successfully."
                : "CSS minified successfully.",
            outputLanguage: "css",
            outputExtension: "css",
          });
          return;
        }

        if (activeTool === "javascript") {
          validateJavaScript(input);
          if (action === "validate") {
            updateToolState(activeTool, {
              output: input,
              error: null,
              notice: "JavaScript syntax looks valid.",
              outputLanguage: "javascript",
              outputExtension: "js",
            });
            return;
          }

          const output =
            action === "format"
              ? await formatWithPrettier(input, "babel")
              : (
                  await minifyJs(input, {
                    compress: true,
                    mangle: false,
                    format: {
                      comments: false,
                    },
                  })
                ).code || "";

          updateToolState(activeTool, {
            output,
            error: null,
            notice:
              action === "format"
                ? "JavaScript formatted successfully."
                : "JavaScript minified successfully.",
            outputLanguage: "javascript",
            outputExtension: "js",
          });
          return;
        }

        if (activeTool === "base64") {
          if (action === "validate") {
            try {
              base64ToUtf8(input.trim());
              updateToolState(activeTool, {
                output: input.trim(),
                error: null,
                notice: "Valid Base64 string.",
                outputLanguage: "plaintext",
                outputExtension: "txt",
              });
            } catch (_error) {
              updateToolState(activeTool, {
                error: "Invalid Base64 content.",
                notice: null,
              });
            }
            return;
          }

          if (action === "format") {
            updateToolState(activeTool, {
              output: utf8ToBase64(input),
              error: null,
              notice: "Encoded to Base64 successfully.",
              outputLanguage: "plaintext",
              outputExtension: "txt",
            });
            return;
          }

          updateToolState(activeTool, {
            output: base64ToUtf8(input.trim()),
            error: null,
            notice: "Decoded from Base64 successfully.",
            outputLanguage: "plaintext",
            outputExtension: "txt",
          });
        }
      } catch (error) {
        updateToolState(activeTool, {
          error:
            error instanceof Error
              ? error.message
              : `Unable to ${action} the current input.`,
          notice: null,
        });
      } finally {
        setBusy(false);
      }
    },
    [
      activeTool,
      currentToolState.input,
      currentToolState.output,
      formatWithPrettier,
      indent,
      updateToolState,
    ],
  );

  useEffect(() => {
    actionRef.current = runAction;
  }, [runAction]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
        event.preventDefault();
        void actionRef.current?.("format");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (activeTool !== "json" || !diffMode) {
      setCompareOutput("");
      setCompareError(null);
      return;
    }

    const validation = validateJsonInput(compareInput);
    if (!validation.valid) {
      setCompareError(validation.message);
      setCompareOutput("");
      return;
    }

    const output = JSON.stringify(
      JSON.parse(compareInput),
      null,
      getIndentString(indent),
    );
    setCompareOutput(output);
    setCompareError(null);
  }, [activeTool, compareInput, diffMode, indent]);

  const handleCopy = async () => {
    const targetValue =
      activeEditor === "input"
        ? currentToolState.input
        : activeEditor === "compare"
          ? compareInput
          : currentToolState.output;

    if (!targetValue) {
      setPageMessage("Nothing to copy yet.");
      return;
    }

    await navigator.clipboard.writeText(targetValue);
    setPageMessage(
      `${activeEditor.charAt(0).toUpperCase() + activeEditor.slice(1)} copied to clipboard.`,
    );
  };

  const handleClear = () => {
    updateToolState(activeTool, {
      input: "",
      output: "",
      error: null,
      notice: "Cleared.",
      outputLanguage: undefined,
      outputExtension: undefined,
    });
    setPageMessage("Input and output cleared.");
  };

  const handleDownload = () => {
    if (!currentToolState.output.trim()) {
      setPageMessage("Format or convert content before downloading.");
      return;
    }

    const extension =
      currentToolState.outputExtension ?? toolDefinition.extension;
    const nameBase = currentToolState.lastLoadedFileName
      ? currentToolState.lastLoadedFileName.replace(/\.[^.]+$/, "")
      : activeTool;

    triggerDownload(
      currentToolState.output,
      `${nameBase}-formatted.${extension}`,
    );
    setPageMessage("Formatted output downloaded.");
  };

  const handleUndoRedo = (type: "undo" | "redo") => {
    const editorRef =
      activeEditor === "input"
        ? inputEditorRef.current
        : activeEditor === "compare"
          ? compareEditorRef.current
          : outputEditorRef.current;

    editorRef?.trigger?.("toolbar", type, null);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleLoadFile = async (file: File) => {
    const text = await file.text();
    const detectedTool = inferToolFromFileName(file.name);
    if (detectedTool) {
      setActiveTool(detectedTool);
    }

    const targetTool = detectedTool ?? activeTool;
    updateToolState(targetTool, (current) => ({
      ...current,
      input: text,
      output: "",
      error: null,
      notice: `${file.name} loaded locally.`,
      lastLoadedFileName: file.name,
      outputLanguage: undefined,
      outputExtension: undefined,
    }));
    setPageMessage(`${file.name} loaded.`);
  };

  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      await handleLoadFile(file);
    }
    event.target.value = "";
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      await handleLoadFile(file);
    }
  };

  const handleConvertJson = () => {
    const validation = validateJsonInput(currentToolState.input);
    if (!validation.valid) {
      updateToolState("json", {
        error: validation.message,
        notice: null,
      });
      return;
    }

    const parsed = JSON.parse(currentToolState.input);
    let output = "";
    let outputLanguage = "plaintext";
    let outputExtension = "txt";

    if (jsonConvertTarget === "yaml") {
      output = yaml.dump(parsed, {
        indent: indent === "tab" ? 2 : Number(indent),
        noRefs: true,
      });
      outputLanguage = "yaml";
      outputExtension = "yaml";
    }

    if (jsonConvertTarget === "csv") {
      output = convertJsonToCsv(parsed);
      outputLanguage = "plaintext";
      outputExtension = "csv";
    }

    if (jsonConvertTarget === "typescript") {
      output = convertJsonToTypeScript(parsed);
      outputLanguage = "typescript";
      outputExtension = "ts";
    }

    updateToolState("json", {
      output,
      error: null,
      notice: `Converted JSON to ${jsonConvertTarget.toUpperCase()}.`,
      outputLanguage,
      outputExtension,
    });
    setJsonViewMode("raw");
  };

  const togglePath = (path: string) => {
    setExpandedPaths((current) => {
      const next = new Set(current);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const expandAllTreeNodes = () => {
    if (!jsonTreeData) return;
    setExpandedPaths(new Set(collectTreePaths(jsonTreeData)));
  };

  const collapseTreeNodes = () => {
    setExpandedPaths(new Set(["root"]));
  };

  const handleEditorMount = (
    editor: any,
    monaco: any,
    target: ActiveEditor,
  ) => {
    monacoRef.current = monaco;

    if (target === "input") {
      inputEditorRef.current = editor;
      editor.onDidFocusEditorText(() => setActiveEditor("input"));
      editor.onDidPaste?.(() => {
        if (formatOnPaste) {
          window.setTimeout(() => {
            void actionRef.current?.("format");
          }, 40);
        }
      });
    }

    if (target === "output") {
      outputEditorRef.current = editor;
      editor.onDidFocusEditorText(() => setActiveEditor("output"));
    }

    if (target === "compare") {
      compareEditorRef.current = editor;
      editor.onDidFocusEditorText(() => setActiveEditor("compare"));
    }
  };

  const primaryActionLabel = activeTool === "base64" ? "Encode" : "Format";
  const secondaryActionLabel = activeTool === "base64" ? "Decode" : "Minify";

  const editorOptions = {
    fontSize: 14,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    wordWrap: "on" as const,
    automaticLayout: true,
    tabSize: indent === "tab" ? 2 : Number(indent),
    formatOnPaste: false,
    bracketPairColorization: { enabled: true },
    guides: { bracketPairs: true },
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Container maxWidth="xl" sx={{ py: { xs: 2.5, sm: 3.5 }, flex: 1 }}>
        <Stack spacing={3}>
          <Box>
            <Typography
              component="h1"
              variant="h4"
              sx={{ fontWeight: 800, mb: 1 }}
            >
              JSON Formatter & Developer Code Formatter Toolkit
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 1100 }}
            >
              A lightweight browser-based developer IDE for formatting,
              validating, minifying and comparing structured data and code. Use
              Monaco editors, JSON tree view, JSON diff mode, file upload,
              client-side conversions and copy-friendly outputs without any
              server interaction.
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 1.5 }}
            >
              <Chip
                label="Client-side only"
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip label="Monaco editors" size="small" variant="outlined" />
              <Chip label="JSON tree + diff" size="small" variant="outlined" />
              <Chip
                label="XML / HTML / CSS / JS / Base64"
                size="small"
                variant="outlined"
              />
              <Chip
                label="Copy and download friendly"
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>

          <Card variant="outlined">
            <CardContent sx={{ p: { xs: 1.75, sm: 2 } }}>
              <Stack spacing={1.5}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.25}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button
                      variant="contained"
                      startIcon={<AutoFixHighIcon />}
                      onClick={() => void runAction("format")}
                      disabled={busy}
                    >
                      {primaryActionLabel}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CompressIcon />}
                      onClick={() => void runAction("minify")}
                      disabled={busy || !toolDefinition.supportsMinify}
                    >
                      {secondaryActionLabel}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<FactCheckIcon />}
                      onClick={() => void runAction("validate")}
                      disabled={busy || !toolDefinition.supportsValidate}
                    >
                      Validate
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={handleClear}
                      disabled={busy}
                    >
                      Clear
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => void handleCopy()}
                    >
                      Copy
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownload}
                    >
                      Download
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      onClick={handleUploadClick}
                    >
                      Upload File
                    </Button>
                  </Stack>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Tooltip title="Undo on focused editor">
                      <span>
                        <IconButton onClick={() => handleUndoRedo("undo")}>
                          <UndoIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Redo on focused editor">
                      <span>
                        <IconButton onClick={() => handleUndoRedo("redo")}>
                          <RedoIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <ToggleButtonGroup
                      size="small"
                      value={editorTheme}
                      exclusive
                      onChange={(_event, value) =>
                        value && setEditorTheme(value)
                      }
                    >
                      <ToggleButton value="light">
                        <LightModeIcon fontSize="small" />
                      </ToggleButton>
                      <ToggleButton value="dark">
                        <DarkModeIcon fontSize="small" />
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  alignItems="center"
                >
                  <Chip
                    size="small"
                    label={`${toolDefinition.label} active`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label="Ctrl/Cmd + Enter formats"
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      formatOnPaste
                        ? "Format on paste enabled"
                        : "Manual format mode"
                    }
                  />
                  {activeTool === "json" && (
                    <Chip
                      size="small"
                      color={jsonValidation?.valid ? "success" : "error"}
                      variant="outlined"
                      label={
                        jsonValidation?.valid
                          ? "Valid JSON detected"
                          : "JSON needs validation"
                      }
                    />
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {pageMessage && (
            <Alert severity="success" onClose={() => setPageMessage(null)}>
              {pageMessage}
            </Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: sidebarOpen
                  ? "260px minmax(0, 1fr)"
                  : "72px minmax(0, 1fr)",
              },
              gap: 2,
              alignItems: "start",
            }}
          >
            <Card
              variant="outlined"
              sx={{ position: { sm: "sticky" }, top: { sm: 88 } }}
            >
              <CardContent sx={{ p: 1.25 }}>
                <Stack spacing={1.25}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {sidebarOpen && (
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Formatter tools
                      </Typography>
                    )}
                    <IconButton
                      onClick={() => setSidebarOpen((current) => !current)}
                      size="small"
                    >
                      {sidebarOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Stack>

                  <Collapse
                    in={sidebarOpen}
                    orientation="vertical"
                    collapsedSize={0}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Switch tools instantly. All editors and formatting run
                      locally.
                    </Typography>
                  </Collapse>

                  <List disablePadding>
                    {TOOL_DEFINITIONS.map((tool) => (
                      <ListItemButton
                        key={tool.id}
                        selected={activeTool === tool.id}
                        onClick={() => setActiveTool(tool.id)}
                        sx={{
                          borderRadius: 2,
                          mb: 0.5,
                          alignItems: "flex-start",
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: sidebarOpen ? 38 : 0 }}>
                          {tool.icon}
                        </ListItemIcon>
                        {sidebarOpen && (
                          <ListItemText
                            primary={tool.label}
                            secondary={tool.description}
                            primaryTypographyProps={{
                              fontWeight: 700,
                              fontSize: 14,
                            }}
                            secondaryTypographyProps={{ fontSize: 12 }}
                          />
                        )}
                      </ListItemButton>
                    ))}
                  </List>

                  {sidebarOpen && (
                    <>
                      <Divider />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formatOnPaste}
                            onChange={(event) =>
                              setFormatOnPaste(event.target.checked)
                            }
                          />
                        }
                        label="Format on paste"
                      />
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "minmax(0, 1fr) minmax(0, 1fr)",
                  },
                  gap: 2,
                }}
              >
                <Card
                  variant="outlined"
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={handleDrop}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700 }}
                        >
                          Input editor
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Paste or drop .json, .txt, .xml, .html, .css or .js
                          files here.
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={toolDefinition.language}
                      />
                    </Stack>
                    <Editor
                      height="520px"
                      language={toolDefinition.language}
                      value={currentToolState.input}
                      theme={editorTheme === "dark" ? "vs-dark" : "vs"}
                      options={editorOptions}
                      onMount={(editor, monaco) =>
                        handleEditorMount(editor, monaco, "input")
                      }
                      onChange={(value) =>
                        updateToolState(activeTool, {
                          input: value ?? "",
                          error: null,
                          notice:
                            activeTool === "json" && value
                              ? validateJsonInput(value).message
                              : currentToolState.notice,
                        })
                      }
                    />
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent sx={{ p: 0 }}>
                    <Stack spacing={0}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          px: 1.5,
                          py: 1.25,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.25}
                          alignItems="center"
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700 }}
                          >
                            Output
                          </Typography>
                          {activeTool === "json" && (
                            <ToggleButtonGroup
                              size="small"
                              value={jsonViewMode}
                              exclusive
                              onChange={(_event, value) =>
                                value && setJsonViewMode(value)
                              }
                            >
                              <ToggleButton value="raw">Raw</ToggleButton>
                              <ToggleButton value="tree">
                                <AccountTreeIcon
                                  fontSize="small"
                                  sx={{ mr: 0.5 }}
                                />{" "}
                                Tree
                              </ToggleButton>
                            </ToggleButtonGroup>
                          )}
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          {activeTool === "json" && jsonViewMode === "tree" && (
                            <>
                              <Button size="small" onClick={expandAllTreeNodes}>
                                Expand all
                              </Button>
                              <Button size="small" onClick={collapseTreeNodes}>
                                Collapse all
                              </Button>
                            </>
                          )}
                          <Chip
                            size="small"
                            variant="outlined"
                            label={currentOutputLanguage}
                          />
                        </Stack>
                      </Stack>

                      {activeTool === "json" && (
                        <Box
                          sx={{
                            px: 1.5,
                            py: 1,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.25}
                            alignItems={{ xs: "stretch", sm: "center" }}
                          >
                            <TextField
                              fullWidth
                              size="small"
                              label="Search inside JSON"
                              value={jsonSearchQuery}
                              onChange={(event) =>
                                setJsonSearchQuery(event.target.value)
                              }
                              InputProps={{
                                startAdornment: (
                                  <SearchIcon
                                    fontSize="small"
                                    sx={{ mr: 1, color: "text.secondary" }}
                                  />
                                ),
                              }}
                            />
                            {activeTool === "json" && (
                              <FormControl size="small" sx={{ minWidth: 170 }}>
                                <InputLabel id="indent-select-label">
                                  Indentation
                                </InputLabel>
                                <Select
                                  labelId="indent-select-label"
                                  label="Indentation"
                                  value={indent}
                                  onChange={(event) =>
                                    setIndent(
                                      event.target.value as IndentOption,
                                    )
                                  }
                                >
                                  <MenuItem value="2">2 spaces</MenuItem>
                                  <MenuItem value="4">4 spaces</MenuItem>
                                  <MenuItem value="tab">Tabs</MenuItem>
                                </Select>
                              </FormControl>
                            )}
                          </Stack>
                        </Box>
                      )}

                      {activeTool === "json" && jsonViewMode === "tree" ? (
                        <Box
                          sx={{
                            height: 520,
                            overflow: "auto",
                            px: 1.5,
                            py: 1.25,
                            backgroundColor: "background.paper",
                          }}
                        >
                          {jsonTreeData !== null ? (
                            <JsonTreeNode
                              value={jsonTreeData}
                              path="root"
                              depth={0}
                              expandedPaths={expandedPaths}
                              togglePath={togglePath}
                              searchQuery={jsonSearchQuery}
                            />
                          ) : (
                            <Alert severity="info">
                              Format or validate valid JSON to browse the tree
                              view.
                            </Alert>
                          )}
                        </Box>
                      ) : (
                        <Editor
                          height="520px"
                          language={currentOutputLanguage}
                          value={currentToolState.output}
                          theme={editorTheme === "dark" ? "vs-dark" : "vs"}
                          options={{ ...editorOptions, readOnly: true }}
                          onMount={(editor, monaco) =>
                            handleEditorMount(editor, monaco, "output")
                          }
                        />
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "minmax(0, 1.4fr) minmax(320px, 0.7fr)",
                  },
                  gap: 2,
                }}
              >
                <Stack spacing={2}>
                  {activeTool === "json" && (
                    <Card variant="outlined">
                      <CardContent sx={{ p: { xs: 1.75, sm: 2 } }}>
                        <Stack spacing={1.5}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "center" }}
                            justifyContent="space-between"
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <DifferenceIcon color="primary" />
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 700 }}
                              >
                                JSON conversions and diff mode
                              </Typography>
                            </Stack>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={diffMode}
                                  onChange={(event) =>
                                    setDiffMode(event.target.checked)
                                  }
                                />
                              }
                              label="Enable diff mode"
                            />
                          </Stack>

                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "center" }}
                          >
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                              <InputLabel id="json-convert-label">
                                Convert JSON to
                              </InputLabel>
                              <Select
                                labelId="json-convert-label"
                                label="Convert JSON to"
                                value={jsonConvertTarget}
                                onChange={(event) =>
                                  setJsonConvertTarget(
                                    event.target.value as JsonConvertTarget,
                                  )
                                }
                              >
                                <MenuItem value="yaml">YAML</MenuItem>
                                <MenuItem value="csv">CSV</MenuItem>
                                <MenuItem value="typescript">
                                  TypeScript interface
                                </MenuItem>
                              </Select>
                            </FormControl>
                            <Button
                              variant="outlined"
                              onClick={handleConvertJson}
                            >
                              Convert
                            </Button>
                          </Stack>

                          {diffMode && (
                            <Stack spacing={1.25}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Compare the current JSON input with a second
                                JSON payload. Both sides are normalized with
                                your selected indentation before diffing.
                              </Typography>
                              <Editor
                                height="220px"
                                language="json"
                                value={compareInput}
                                theme={
                                  editorTheme === "dark" ? "vs-dark" : "vs"
                                }
                                options={editorOptions}
                                onMount={(editor, monaco) =>
                                  handleEditorMount(editor, monaco, "compare")
                                }
                                onChange={(value) =>
                                  setCompareInput(value ?? "")
                                }
                              />
                              {compareError ? (
                                <Alert severity="error">{compareError}</Alert>
                              ) : (
                                <DiffEditor
                                  height="280px"
                                  language="json"
                                  theme={
                                    editorTheme === "dark" ? "vs-dark" : "vs"
                                  }
                                  original={
                                    validateJsonInput(currentToolState.input)
                                      .valid
                                      ? JSON.stringify(
                                          JSON.parse(currentToolState.input),
                                          null,
                                          getIndentString(indent),
                                        )
                                      : currentToolState.input
                                  }
                                  modified={compareOutput}
                                  options={{
                                    renderSideBySide: true,
                                    readOnly: true,
                                    minimap: { enabled: false },
                                    automaticLayout: true,
                                  }}
                                />
                              )}
                            </Stack>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  )}

                  <Card variant="outlined">
                    <CardContent sx={{ p: { xs: 1.75, sm: 2 } }}>
                      <Stack spacing={1.5}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700 }}
                        >
                          Privacy-first developer workflow
                        </Typography>
                        <Alert severity="info">
                          All formatting, validation, conversions, search, JSON
                          tree rendering and diffing happen locally in your
                          browser. No payloads are uploaded to a server.
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                          This toolkit is designed for API payloads, config
                          files, code snippets and structured documents that
                          need quick formatting without leaving the current
                          browser tab.
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Stack>

                <Stack spacing={2}>
                  {activeTool === "json" && (
                    <Card variant="outlined">
                      <CardContent sx={{ p: { xs: 1.75, sm: 2 } }}>
                        <Stack spacing={1.25}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700 }}
                          >
                            JSON insights
                          </Typography>
                          {jsonMetadata ? (
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "1fr 1fr",
                                  sm: "1fr 1fr",
                                },
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  p: 1.1,
                                  borderRadius: 2,
                                  backgroundColor: "background.default",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Total keys
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {jsonMetadata.totalKeys}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  p: 1.1,
                                  borderRadius: 2,
                                  backgroundColor: "background.default",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Objects
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {jsonMetadata.objectCount}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  p: 1.1,
                                  borderRadius: 2,
                                  backgroundColor: "background.default",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Arrays / items
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {jsonMetadata.arrayCount} /{" "}
                                  {jsonMetadata.totalArrayItems}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  p: 1.1,
                                  borderRadius: 2,
                                  backgroundColor: "background.default",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Max depth
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {jsonMetadata.maxDepth}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  p: 1.1,
                                  borderRadius: 2,
                                  backgroundColor: "background.default",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Largest array
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {jsonMetadata.maxArraySize}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  p: 1.1,
                                  borderRadius: 2,
                                  backgroundColor: "background.default",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Primitives
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {jsonMetadata.primitiveCount}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Alert severity="warning">
                              Provide valid JSON to inspect keys, array sizes
                              and nesting depth.
                            </Alert>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  )}

                  <Card variant="outlined">
                    <CardContent sx={{ p: { xs: 1.75, sm: 2 } }}>
                      <Stack spacing={1.25}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700 }}
                        >
                          Tool settings
                        </Typography>
                        <FormControl fullWidth size="small">
                          <InputLabel id="indent-toolbar-label">
                            Indentation
                          </InputLabel>
                          <Select
                            labelId="indent-toolbar-label"
                            value={indent}
                            label="Indentation"
                            onChange={(event) =>
                              setIndent(event.target.value as IndentOption)
                            }
                          >
                            <MenuItem value="2">2 spaces</MenuItem>
                            <MenuItem value="4">4 spaces</MenuItem>
                            <MenuItem value="tab">Tabs</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formatOnPaste}
                              onChange={(event) =>
                                setFormatOnPaste(event.target.checked)
                              }
                            />
                          }
                          label="Format on paste"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={diffMode}
                              onChange={(event) =>
                                setDiffMode(event.target.checked)
                              }
                              disabled={activeTool !== "json"}
                            />
                          }
                          label="JSON diff mode"
                        />
                        <Typography variant="caption" color="text.secondary">
                          Use Ctrl/Cmd + Enter to run the primary format action
                          from anywhere on the page.
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>

                  {(currentToolState.error || currentToolState.notice) && (
                    <Alert
                      severity={currentToolState.error ? "error" : "success"}
                    >
                      {currentToolState.error ?? currentToolState.notice}
                    </Alert>
                  )}
                </Stack>
              </Box>

              <Box component="section">
                <Card variant="outlined">
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack spacing={3}>
                      <Box>
                        <Typography
                          component="h2"
                          variant="h5"
                          sx={{ fontWeight: 800, mb: 1 }}
                        >
                          Built for fast developer formatting and validation
                          workflows
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          The Developer Formatter Toolkit is designed for
                          developers who need a fast, static-site-friendly
                          utility for working with JSON, XML, HTML, CSS,
                          JavaScript and Base64. It keeps a familiar IDE-style
                          workflow with Monaco editors, keyboard shortcuts, raw
                          and tree views, diff mode and file-friendly actions
                          such as upload, copy and download.
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 3,
                        }}
                      >
                        <Box>
                          <Typography
                            component="h3"
                            variant="subtitle1"
                            sx={{ fontWeight: 700, mb: 1.25 }}
                          >
                            Why developers use this toolkit
                          </Typography>
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              • Client-side processing keeps API payloads and
                              code snippets private.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              • JSON tree view and diff mode make nested
                              structures easier to inspect.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              • Monaco editors provide syntax highlighting, line
                              numbers and bracket matching.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              • Shared toolbar actions speed up formatting,
                              validation and export workflows.
                            </Typography>
                          </Stack>
                        </Box>
                        <Box>
                          <Typography
                            component="h3"
                            variant="subtitle1"
                            sx={{ fontWeight: 700, mb: 1.25 }}
                          >
                            Included formatter tools
                          </Typography>
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              • JSON formatter, validator, minifier, tree
                              viewer, search and conversion tools.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              • XML formatter and validator for API responses
                              and configuration files.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              • HTML, CSS and JavaScript formatters with compact
                              output support.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              • Base64 encoder and decoder for quick text
                              transforms.
                            </Typography>
                          </Stack>
                        </Box>
                      </Box>

                      <Divider />

                      <Box>
                        <Typography
                          component="h2"
                          variant="h5"
                          sx={{ fontWeight: 800, mb: 2 }}
                        >
                          Frequently asked questions
                        </Typography>
                        <Stack spacing={2}>
                          {FAQ_ITEMS.map((item) => (
                            <Box key={item.question}>
                              <Typography
                                component="h3"
                                variant="subtitle1"
                                sx={{ fontWeight: 700, mb: 0.5 }}
                              >
                                {item.question}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item.answer}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Container>

      <Box
        sx={{
          ".json-search-highlight": {
            backgroundColor: "rgba(145, 189, 58, 0.28)",
            borderRadius: "2px",
          },
        }}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept=".json,.txt,.xml,.html,.htm,.css,.js,.mjs,.cjs"
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />

      <Footer />
      <BackToWorldButton />
    </Box>
  );
};

export default DeveloperFormatterToolkit;
